const WISHLIST_KEY = 'onstara_wishlist';

const Wishlist = {
  getItems() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch { return []; }
  },
  saveItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    this.syncUI();
  },
  addItem(handle) {
    const items = this.getItems();
    if (!items.includes(handle)) { items.push(handle); this.saveItems(items); }
  },
  removeItem(handle) {
    this.saveItems(this.getItems().filter(h => h !== handle));
  },
  hasItem(handle) {
    return this.getItems().includes(handle);
  },
  toggleItem(handle) {
    this.hasItem(handle) ? this.removeItem(handle) : this.addItem(handle);
    return this.hasItem(handle);
  },
  count() { return this.getItems().length; },
  syncUI() {
    const count = this.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const active = this.hasItem(btn.dataset.wishlistToggle);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }
};

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

function getImageUrl(url, width = 600) {
  if (!url) return null;
  const src = url.startsWith('//') ? 'https:' + url : url;
  return src.replace(/(\.(jpg|jpeg|png|gif|webp))/, `_${width}x$1`);
}

function productCardHTML(product) {
  if (!product) return '';
  const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
  const variantId = variant ? variant.id : '';
  const price = formatMoney(product.price || 0);
  const comparePrice = product.compare_at_price > product.price ? formatMoney(product.compare_at_price) : null;
  const image = product.featured_image || (product.images && product.images[0]);
  const imgSrc = getImageUrl(image);
  const available = product.available;

  return `
    <div class="wl-card" data-handle="${product.handle}">
      <div class="wl-card__media">
        <a href="/products/${product.handle}" class="wl-card__img-link">
          ${imgSrc
            ? `<img src="${imgSrc}" alt="${product.title}" loading="lazy" class="wl-card__img">`
            : `<div class="wl-card__img-placeholder"></div>`}
        </a>
        ${!available ? `<span class="wl-card__badge">Sold Out</span>` : ''}
        <button class="wl-card__remove" data-remove="${product.handle}" aria-label="Remove from wishlist">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="wl-card__body">
        <a href="/products/${product.handle}" class="wl-card__title">${product.title}</a>
        <div class="wl-card__price-row">
          <span class="wl-card__price">${price}</span>
          ${comparePrice ? `<span class="wl-card__compare">${comparePrice}</span>` : ''}
        </div>
        ${available && variantId
          ? `<button class="wl-card__atc" data-variant="${variantId}">Add to Cart</button>`
          : `<button class="wl-card__atc wl-card__atc--disabled" disabled>Sold Out</button>`}
      </div>
    </div>`;
}

async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

async function addToCart(variantId, btn) {
  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = 'Adding…';
  
  try {
    const cartItemsComponents = document.querySelectorAll('cart-items-component, cart-drawer');
    const sectionsToUpdate = [];
    cartItemsComponents.forEach(component => {
      if (component.dataset.sectionId) {
        sectionsToUpdate.push(component.dataset.sectionId);
      }
    });
    
    // Also add the cart bubble/icon section if we know its name. Usually it's 'cart-bubble' or 'cart-icon-bubble'
    sectionsToUpdate.push('cart-bubble');
    sectionsToUpdate.push('cart-drawer');

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        items: [{ id: variantId, quantity: 1 }],
        sections: [...new Set(sectionsToUpdate)].join(',')
      })
    });
    
    if (res.ok) {
      const responseJson = await res.json();
      btn.textContent = 'Added ✓';
      btn.classList.add('wl-card__atc--added');
      
      const cartData = await fetch('/cart.js').then(r => r.json());
      
      // Dispatch Horizon theme event
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: variantId,
          data: {
            source: 'product-form-component',
            sections: responseJson.sections,
            itemCount: 1
          }
        }
      }));

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.classList.remove('wl-card__atc--added');
      }, 2000);
    } else {
      throw new Error('Failed to add to cart');
    }
  } catch (e) {
    console.error(e);
    btn.textContent = 'Error';
    setTimeout(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    }, 2000);
  }
}

class WishlistPage {
  constructor() {
    this.productsEl = document.getElementById('wl-products');
    this.emptyEl = document.getElementById('wl-empty');
    this.gridEl = document.getElementById('wl-grid');
    this.countEl = document.getElementById('wl-count');
    if (this.productsEl) this.init();
  }

  async init() {
    const handles = Wishlist.getItems();
    if (!handles.length) { this.showEmpty(); return; }
    this.showLoading();
    const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
    if (!products.length) { this.showEmpty(); return; }
    this.renderGrid(products);
  }

  showEmpty() {
    if (this.emptyEl) this.emptyEl.hidden = false;
    if (this.gridEl) this.gridEl.hidden = true;
    if (this.countEl) this.countEl.textContent = '0 saved items';
  }

  showLoading() {
    if (this.productsEl) this.productsEl.innerHTML = `
      <div class="wl-loading">
        <div class="wl-loading__spinner"></div>
        <p>Loading your wishlist…</p>
      </div>`;
  }

  renderGrid(products) {
    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.gridEl) this.gridEl.hidden = false;
    if (this.countEl) this.countEl.textContent = `${products.length} saved item${products.length === 1 ? '' : 's'}`;
    
    try {
      if (this.productsEl) this.productsEl.innerHTML = products.map(productCardHTML).join('');
    } catch (e) {
      console.error('Wishlist render error:', e);
    }

    this.productsEl.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.remove;
        Wishlist.removeItem(handle);
        const card = btn.closest('.wl-card');
        card.style.opacity = '0';
        card.style.transform = 'scale(0.95)';
        card.style.transition = 'all 0.25s ease';
        setTimeout(() => {
          card.remove();
          const remaining = this.productsEl.querySelectorAll('.wl-card').length;
          if (remaining === 0) this.showEmpty();
          if (this.countEl) this.countEl.textContent = `${remaining} saved item${remaining === 1 ? '' : 's'}`;
        }, 250);
      });
    });

    this.productsEl.querySelectorAll('[data-variant]').forEach(btn => {
      btn.addEventListener('click', () => addToCart(btn.dataset.variant, btn));
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  Wishlist.syncUI();
  new WishlistPage();
});

document.addEventListener('click', (e) => {
  const toggleBtn = e.target.closest('[data-wishlist-toggle]');
  if (toggleBtn) {
    e.preventDefault();
    const handle = toggleBtn.dataset.wishlistToggle;
    Wishlist.toggleItem(handle);
  }
});

window.Wishlist = Wishlist;
